import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { StudentData } from "../../../../types/classTypes";
import { ExamSession, MarkPayload } from "../../../../types/markTypes";
import { getAssignedStudents } from "../../../../services/classService";
import { creatMarks, getExamSessionById, getMarksByClassAndExamSession } from "../../../../services/markService";
import { useNavigate } from "react-router";

export const useClassMarks = (classId: string, examSessionId: string) => {
  const [students, setStudents] = useState<StudentData[]>([]);
  const [marks, setMarks] = useState<Record<string, number | null>>({});
  const [examSession, setExamSession] = useState<ExamSession | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const navigate = useNavigate()
  /**
   * 🔹 Fetch students assigned to the class
   */
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsData, examSessionData, marksData] = await Promise.all([
        getAssignedStudents(classId),
        getExamSessionById(examSessionId),
        getMarksByClassAndExamSession(examSessionId, classId),
      ]);
  
      setStudents(studentsData);
      setExamSession(examSessionData.data);
  
      // Map fetched marks to state
      const fetchedMarks = marksData.data.reduce((acc, mark) => {
        acc[mark.student.id] = mark.mark;
        return acc;
      }, {} as Record<string, number>);
      setMarks(fetchedMarks);
  
    } catch (error) {
      message.error("Failed to fetch marks or students.");
    } finally {
      setLoading(false);
    }
  }, [classId, examSessionId]);

  /**
   * 🔹 Initialize marks for students on first load
   */
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  /**
   * 🔹 Handle input change for marks
   */
  const handleMarkChange = (studentId: string, value: string) => {
    setMarks((prev) => ({
      ...prev,
      [studentId]: value ? parseFloat(value) || 0 : null,
    }));
  };

  /**
   * 🔹 Submit marks for all students
   */
  const handleSubmitMarks = async () => {
    if (!examSessionId || !classId) {
      message.error("Invalid class or exam session.");
      return;
    }

    // ✅ Filter out students without marks (optional, depends on requirement)
    const payload: MarkPayload[] = students
      .filter((student) => marks[student.id] !== undefined && marks[student.id] !== null)
      .map((student) => ({
        studentId: student.id,
        examSessionId,
        mark: marks[student.id]!,
      }));

    if (payload.length === 0) {
      message.warning("No marks entered to submit.");
      return;
    }

    setSubmitting(true);
    try {
      await creatMarks(payload);
      message.success("Marks submitted successfully.");
      navigate(-1)
    } catch (error) {
      message.error("Failed to submit marks.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    students,
    marks,
    examSession,
    loading,
    submitting,
    handleMarkChange,
    handleSubmitMarks,
  };
};
